// Popover that opens when a pill is clicked. The C1, B1 and A1 pills open a
// read-only notice instead: they follow the deal header (owner rulings,
// 2026-10-09).
//
// Layout:
//   Header — role code + role label.
//   Status radio — Unassigned / Assigned / Not applicable for this deal.
//   User dropdown — role-filtered users (visible only when status=Assigned).
//   Self-claim shortcut — primary button shown when pill is Unassigned and
//                          the caller's role matches.
//   Footer — Cancel + Save (Save fires PUT
//            /api/deals/{dealId}/assignments/{roleCode}).

import { useEffect, useRef, useState, type ReactNode } from "react";
import { Button } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { ApiError } from "@/api/client";
import { RoleFilteredUserPicker } from "@/components/DealFlow/common/RoleFilteredUserPicker";
import {
  useDealOutcome,
  useUpdateAssignment,
  useMarkSlotNotApplicable,
} from "@/hooks/dealFlow/useDealFlow";
import {
  DEAL_ROLE_LABEL,
  type DealAssignmentDto,
  type DealAssignmentStatus,
  type DealRoleCode,
} from "@/api/dealFlow/types";
import {
  canAssignPill,
  canClearOwnPill,
  canMarkSlotNotApplicable,
  canSelfClaim,
  isLaneApplicabilityLocked,
  isPillEditable,
} from "@/lib/dealFlowPermissions";

interface DealAssignmentPopoverProps {
  dealId: string;
  role: DealRoleCode;
  assignment: DealAssignmentDto | undefined;
  /** The current holder's name when the pill is Assigned. */
  assigneeName?: string | null;
  trigger: ReactNode;
}

export function DealAssignmentPopover({
  dealId,
  role,
  assignment,
  assigneeName = null,
  trigger,
}: DealAssignmentPopoverProps) {
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  // Close on outside click + Esc.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      const t = e.target as Node;
      if (popoverRef.current?.contains(t) || triggerRef.current?.contains(t)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative inline-block">
      <div ref={triggerRef} onClick={() => setOpen((v) => !v)}>
        {trigger}
      </div>
      {open && (
        <div
          ref={popoverRef}
          className="absolute z-40 top-full left-0 mt-space-1 w-[280px] bg-white-pure border border-border-light rounded-md shadow-lg p-space-3"
          role="dialog"
          aria-label={isPillEditable(role) ? `Assign ${role}` : `${role} assignment`}
        >
          {isPillEditable(role) ? (
            <PopoverContent
              dealId={dealId}
              role={role}
              assignment={assignment}
              onClose={() => setOpen(false)}
            />
          ) : (
            <FollowsDealNotice
              role={role}
              assigneeName={assigneeName}
              onClose={() => setOpen(false)}
            />
          )}
        </div>
      )}
    </div>
  );
}

function PopoverHeader({ role }: { role: DealRoleCode }) {
  return (
    <div>
      <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow">
        {role}
      </div>
      <div className="font-display text-h4 text-onyx">{DEAL_ROLE_LABEL[role]}</div>
    </div>
  );
}

/** Where each header pill's holder is set. Mirrors the server's refusal. */
const FOLLOWS_DEAL_NOTE: Partial<Record<DealRoleCode, string>> = {
  C1:
    "This pill shows the deal's broker liaison. The deal's creator, or its submission's liaison, is its C1; " +
    "to change it, use Reassign C1 in the deal header (Assign C1 when it has none).",
  B1:
    "This pill shows the deal's loan administrator. Assign to B1 on the Intake tab sets it; to change it " +
    "afterwards, use Reassign B1 in the deal header.",
  A1:
    "This pill shows the deal's underwriter. Send to Underwriting assigns A1; to change it afterwards, " +
    "use Reassign A1 in the deal header.",
};

/** A header pill: read-only, with where it is set (owner rulings, 2026-10-09). */
function FollowsDealNotice({
  role,
  assigneeName,
  onClose,
}: {
  role: DealRoleCode;
  assigneeName: string | null;
  onClose: () => void;
}) {
  return (
    <div className="flex flex-col gap-space-3">
      <PopoverHeader role={role} />
      <p className="text-small text-onyx m-0" data-testid="follows-deal-current">
        Currently: {assigneeName ?? "Unassigned"}
      </p>
      <p className="text-small text-gray-mid m-0">{FOLLOWS_DEAL_NOTE[role]}</p>
      <div className="flex items-center justify-end">
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  );
}

interface PopoverContentProps {
  dealId: string;
  role: DealRoleCode;
  assignment: DealAssignmentDto | undefined;
  onClose: () => void;
}

function PopoverContent({ dealId, role, assignment, onClose }: PopoverContentProps) {
  const auth = useAuth();
  const toast = useToast();
  const permSet = new Set(auth.user?.permissions ?? []);
  const userRoleCodes = auth.user?.roleCodes ?? [];

  const currentStatus: DealAssignmentStatus = assignment?.status ?? "Unassigned";
  const currentUserId = assignment?.assignedUserId ?? null;

  const callerIsCurrentAssignee =
    !!auth.user?.id && currentUserId === auth.user.id;

  const canEdit = canAssignPill(permSet);
  const canClearOwn = canClearOwnPill(permSet, { callerIsCurrentAssignee });
  const canNa = canMarkSlotNotApplicable(permSet);
  const canSelfClaimNow =
    currentStatus === "Unassigned" &&
    canSelfClaim(permSet, { roleCode: role, userRoleCodes });

  // A finalized outcome locks the lane pills' Not Applicable status.
  const outcome = useDealOutcome(dealId);
  const applicabilityLocked = isLaneApplicabilityLocked(role, outcome.data?.isFinalized === true);
  const wasNotApplicable = currentStatus === "NotApplicable";

  const [status, setStatus] = useState<DealAssignmentStatus>(currentStatus);
  const [userId, setUserId] = useState<string | null>(currentUserId);

  const update = useUpdateAssignment(dealId);
  const markNa = useMarkSlotNotApplicable(dealId);

  // Save stays off until the choice differs from the pill as it stands; a
  // re-save of the same status changes nothing (staging run, 2026-10-14).
  const unchanged =
    status === currentStatus && (status !== "Assigned" || userId === currentUserId);

  const onSave = async () => {
    try {
      if (status === "NotApplicable") {
        await markNa.mutateAsync(role);
        toast.success(`${role} marked Not Applicable.`);
      } else {
        await update.mutateAsync({
          roleCode: role,
          body: {
            status,
            assignedUserId: status === "Assigned" ? userId : null,
          },
        });
        toast.success(
          status === "Assigned"
            ? `${role} assigned.`
            : `${role} cleared.`,
        );
      }
      onClose();
    } catch (err) {
      toast.error(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : err instanceof Error
            ? err.message
            : "Could not update assignment.",
      );
    }
  };

  const onSelfClaim = async () => {
    if (!auth.user?.id) return;
    try {
      await update.mutateAsync({
        roleCode: role,
        body: { status: "Assigned", assignedUserId: auth.user.id },
      });
      toast.success(`Claimed ${role}.`);
      onClose();
    } catch (err) {
      toast.error(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : "Could not claim slot.",
      );
    }
  };

  return (
    <div className="flex flex-col gap-space-3">
      <PopoverHeader role={role} />

      {canSelfClaimNow && (
        <Button variant="primary" onClick={onSelfClaim} disabled={update.isPending}>
          Claim this slot
        </Button>
      )}

      <fieldset className="flex flex-col gap-space-2 m-0 p-0 border-0">
        <legend className="sr-only">Slot status</legend>
        <RadioRow
          name={`assignment-${role}`}
          value="Unassigned"
          checked={status === "Unassigned"}
          onChange={() => setStatus("Unassigned")}
          disabled={(!canEdit && !canClearOwn) || (applicabilityLocked && wasNotApplicable)}
          label="Unassigned"
        />
        <RadioRow
          name={`assignment-${role}`}
          value="Assigned"
          checked={status === "Assigned"}
          onChange={() => setStatus("Assigned")}
          disabled={!canEdit || (applicabilityLocked && wasNotApplicable)}
          label="Assigned"
        />
        {canNa && (
          <RadioRow
            name={`assignment-${role}`}
            value="NotApplicable"
            checked={status === "NotApplicable"}
            onChange={() => setStatus("NotApplicable")}
            disabled={(!canEdit && !canNa) || (applicabilityLocked && !wasNotApplicable)}
            label="Not applicable for this deal"
          />
        )}
      </fieldset>

      {applicabilityLocked && (
        <p className="text-caption text-gray-mid m-0" data-testid="lane-applicability-locked">
          The deal outcome is finalized, so this lane cannot be marked Not
          Applicable or reopened. Undo the outcome finalize first.
        </p>
      )}

      {status === "Assigned" && (
        <div>
          <label className="block text-caption text-gray-mid mb-space-1">
            User
          </label>
          <RoleFilteredUserPicker
            role={role}
            value={userId}
            onChange={setUserId}
            disabled={!canEdit}
            aria-label={`Select ${role}`}
          />
        </div>
      )}

      <div className="flex items-center justify-end gap-space-2">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={() => void onSave()}
          disabled={
            !(canEdit || (canClearOwn && status === "Unassigned")) ||
            update.isPending ||
            markNa.isPending ||
            unchanged ||
            (status === "Assigned" && !userId)
          }
        >
          {update.isPending || markNa.isPending ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  );
}

function RadioRow({
  name,
  value,
  checked,
  onChange,
  label,
  disabled,
}: {
  name: string;
  value: string;
  checked: boolean;
  onChange: () => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <label
      className={`flex items-center gap-space-2 text-small ${
        disabled ? "opacity-50" : "cursor-pointer"
      }`}
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
      />
      <span>{label}</span>
    </label>
  );
}
